import React, { useState, useEffect } from "react";
import {
  Zap,
  Target,
  Clock,
  AlertCircle,
  BarChart3,
  TrendingUp,
  ExternalLink,
  Menu,
  X,
} from "lucide-react";

export default function Navbar({ wpm, accuracy, errors, time, cpm, kph }) {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if it's mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Close sidebar when clicking on overlay
  const handleSidebarClose = () => {
    setIsMobileOpen(false);
  };

  // Metrics sidebar content component
  const MetricsSidebar = () => (
    <div className="w-80 bg-gray-900 h-full p-6 flex flex-col">
      {/* Close button for mobile */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl text-cyan-400 font-bold">Typing Metrics</h2>
        <button
          onClick={() => setIsMobileOpen(false)}
          className="p-2 text-cyan-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors"
          aria-label="sidebar button"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 gap-4">
        {/* WPM */}
        <div className="bg-black rounded-lg p-4 border border-gray-700 shadow-lg flex items-center space-x-3">
          <Zap className="w-5 h-5 text-green-400" />
          <div className="flex-1">
            <div className="text-green-400 font-bold text-lg">{wpm}</div>
            <div className="text-gray-300 text-sm">Words Per Minute</div>
          </div>
        </div>

        {/* CPM */}
        <div className="bg-black rounded-lg p-4 border border-gray-700 shadow-lg flex items-center space-x-3">
          <TrendingUp className="w-5 h-5 text-blue-400" />
          <div className="flex-1">
            <div className="text-blue-400 font-bold text-lg">{cpm}</div>
            <div className="text-gray-300 text-sm">Characters Per Minute</div>
          </div>
        </div>

        {/* KPH */}
        <div className="bg-black rounded-lg p-4 border border-gray-700 shadow-lg flex items-center space-x-3">
          <BarChart3 className="w-5 h-5 text-purple-400" />
          <div className="flex-1">
            <div className="text-purple-400 font-bold text-lg">{kph}</div>
            <div className="text-gray-300 text-sm">Keystrokes Per Hour</div>
          </div>
        </div>

        {/* Accuracy */}
        <div className="bg-black rounded-lg p-4 border border-gray-700 shadow-lg flex items-center space-x-3">
          <Target className="w-5 h-5 text-yellow-400" />
          <div className="flex-1">
            <div className="text-yellow-400 font-bold text-lg">{accuracy}%</div>
            <div className="text-gray-300 text-sm">Accuracy</div>
          </div>
        </div>

        {/* Errors */}
        <div className="bg-black rounded-lg p-4 border border-gray-700 shadow-lg flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-400" />
          <div className="flex-1">
            <div className="text-red-400 font-bold text-lg">{errors}</div>
            <div className="text-gray-300 text-sm">Errors</div>
          </div>
        </div>

        {/* Time */}
        <div className="bg-black rounded-lg p-4 border border-gray-700 shadow-lg flex items-center space-x-3">
          <Clock className="w-5 h-5 text-cyan-400" />
          <div className="flex-1">
            <div className="text-cyan-400 font-bold text-lg">{time}s</div>
            <div className="text-gray-300 text-sm">Time</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="fixed top-0 left-0 w-full bg-black backdrop-blur-lg border-b border-gray-700 p-3 flex items-center justify-between shadow-2xl z-50 h-16">
        {/* Logo - Shows </> on mobile, full logo on desktop */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            {isMobile ? (
              <div className="text-cyan-400 text-2xl font-bold">{'</>'}</div>
            ) : (
              <img 
                src="/logo.svg" 
                alt="ProgrammerTyping Logo" 
                className="w-60 h-24"
              />
            )}
          </div>
        </div>

        {/* Metrics in the center - Hidden on mobile */}
        <div className="hidden md:flex space-x-3 absolute left-1/2 transform -translate-x-1/2">
          {/* WPM */}
          <div className="bg-black backdrop-blur-sm rounded-lg px-3 py-1 border border-gray-700 shadow-lg min-w-[90px] flex items-center space-x-2">
            <Zap className="w-4 h-4 text-green-400" />
            <div>
              <div className="text-green-400 font-bold text-sm">{wpm}</div>
              <div className="text-gray-300 text-xs">WPM</div>
            </div>
          </div>

          {/* CPM */}
          <div className="bg-black backdrop-blur-sm rounded-lg px-3 py-1 border border-gray-700 shadow-lg min-w-[90px] flex items-center space-x-2">
            <TrendingUp className="w-4 h-4 text-blue-400" />
            <div>
              <div className="text-blue-400 font-bold text-sm">{cpm}</div>
              <div className="text-gray-300 text-xs">CPM</div>
            </div>
          </div>

          {/* KPH */}
          <div className="bg-black backdrop-blur-sm rounded-lg px-3 py-1 border border-gray-700 shadow-lg min-w-[90px] flex items-center space-x-2">
            <BarChart3 className="w-4 h-4 text-purple-400" />
            <div>
              <div className="text-purple-400 font-bold text-sm">{kph}</div>
              <div className="text-gray-300 text-xs">KPH</div>
            </div>
          </div>

          {/* Accuracy */}
          <div className="bg-black backdrop-blur-sm rounded-lg px-3 py-1 border border-gray-700 shadow-lg min-w-[90px] flex items-center space-x-2">
            <Target className="w-4 h-4 text-yellow-400" />
            <div>
              <div className="text-yellow-400 font-bold text-sm">{accuracy}%</div>
              <div className="text-gray-300 text-xs">Accuracy</div>
            </div>
          </div>

          {/* Errors */}
          <div className="bg-black backdrop-blur-sm rounded-lg px-3 py-1 border border-gray-700 shadow-lg min-w-[90px] flex items-center space-x-2">
            <AlertCircle className="w-4 h-4 text-red-400" />
            <div>
              <div className="text-red-400 font-bold text-sm">{errors}</div>
              <div className="text-gray-300 text-xs">Errors</div>
            </div>
          </div>

          {/* Time */}
          <div className="bg-black backdrop-blur-sm rounded-lg px-3 py-1 border border-gray-700 shadow-lg min-w-[90px] flex items-center space-x-2">
            <Clock className="w-4 h-4 text-cyan-400" />
            <div>
              <div className="text-cyan-400 font-bold text-sm">{time}s</div>
              <div className="text-gray-300 text-xs">Time</div>
            </div>
          </div>
        </div>

        {/* Right side - External link button and hamburger menu */}
        <div className="flex items-center space-x-3">
          {/* External link button */}
          <a
            href="https://punote.com"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-linear-to-r from-green-700 to-blue-700 hover:from-green-600 hover:to-blue-700 text-white px-4 py-2 rounded-2xl font-medium flex items-center space-x-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 border border-green-400/30"
          >
            <span className="hidden sm:inline">For Bachelor Students</span>
            <span className="sm:hidden">Students</span>
            <ExternalLink className="w-4 h-4" />
          </a>

          {/* Hamburger menu for mobile metrics */}
          {isMobile && (
            <button
              className="p-2 bg-gray-900 rounded-lg text-cyan-400 border border-gray-700 hover:bg-gray-800 transition-colors"
              aria-label="hamburger menu for metrics"
              onClick={() => setIsMobileOpen(!isMobileOpen)}
            >
              {isMobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" aria-label="hamburger menu" />}
            </button>
          )}
        </div>
      </div>

      {/* Mobile Metrics Sidebar with Overlay */}
      {isMobile && (
        <>
          {/* Overlay */}
          {isMobileOpen && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
              onClick={handleSidebarClose}
            />
          )}
          
          {/* Mobile Sidebar - Slides from right */}
          <div 
            className={`
              fixed top-0 right-0 h-full z-40 md:hidden
              transform transition-transform duration-300 ease-in-out
              ${isMobileOpen ? 'translate-x-0' : 'translate-x-full'}
            `}
          >
            <MetricsSidebar />
          </div>
        </>
      )}
    </>
  );
}